// src/components/CourierBriefing/CourierBriefing.jsx
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  MobileStepper,
  IconButton,
  Stack,
  useTheme,
  useMediaQuery,
  alpha,
} from '@mui/material';
import {
  BookOpen,
  X,
  Truck,
  Banknote,
  Bell,
  CheckCircle2,
  ChevronRight,
  ChevronLeft,
  ArrowRight,
  Smartphone,
  Star,
  User,
  Building2,
  GitBranch,
} from 'lucide-react';
import { updateUserProfile } from '../../firebase/user';

// ─── Step definitions ─────────────────────────────────────────────────────────

const STEPS = [
  {
    icon: Smartphone,
    color: '#2563eb',
    bg:    '#eff6ff',
    darkBg:'#172554',
    title: 'Mikä on Pakettihuolto?',
    intro: 'Pakettihuolto on joukkoistettu kuljetusalusta.',
    sections: [
      {
        term: "Joukkoistaminen",
        body: 'Kuka tahansa voi toimia kuljettajana tai lähettäjänä: yksityishenkilöt, yritykset ja organisaatiot. Verkosto kasvaa käyttäjiensä mukana.',
      },
      {
        term: "Kuljetusalusta",
        body: 'Logististen palveluiden tarjoaminen verkkosovelluksen kautta, jossa koko prosessi — tilauksesta toimitukseen ja laskutukseen — tapahtuu yhdessä paikassa ja kehittyy jatkuvasti yhteisön palautteen pohjalta.',
      },
    ],
  },
  {
    icon: Truck,
    color: '#16a34a',
    bg:    '#f0fdf4',
    darkBg:'#052e16',
    title: 'Miten kuljettaminen toimii?',
    intro: 'Kuljettaja voi tarkastella sovelluksesta kuljetuksia, jotka ovat hänen alueellaan. Hän voi myös jakaa kuljetuksen eteenpäin, mikäli tietää että jokin kuljetus sopisi paremmin jollekulle muulle. Onnistunut referointi tuottaa myös referoijalle tuloja.',
    steps: [
      { icon: Bell,  label: 'Kuljetuksen valinta',          desc: 'Selaa alueesi avoimia toimeksiantoja ja hyväksy sopiva.' },
      { icon: Truck, label: 'Kuljetuksen suorittaminen',    desc: 'Nouto, toimitus ja kuittaus sovelluksessa.' },
      { icon: Star,  label: 'Arviointi (vapaavalintainen)', desc: 'Anna palaute ja merkitse poikkeamat — hintoja voidaan korjata havaintojen perusteella jälkikäteen.' },
    ],
    note: 'Muista: kuljettaminen on asiakaspalvelua. Jokainen erinomaisesti hoidettu toimeksianto lisää asiakastyytyväisyyttä — ja sitä kautta uusia asiakkaita koko verkostolle.',
    noteColor: '#16a34a',
  },
  {
    icon: Banknote,
    color: '#7c3aed',
    bg:    '#f5f3ff',
    darkBg:'#2e1065',
    title: 'Miten palkkiot maksetaan?',
    intro: 'Kuriireille toimitetaan koosteet suoritetuista kuljetuksista, ja niitä vasten maksetaan palkkiot. Maksutapa riippuu siitä, oletko yksityishenkilö vai yritys.',
    paymentRows: [
      {
        icon: User,
        label: 'Yksityishenkilö',
        color: '#2563eb',
        bg:    '#eff6ff',
        darkBg:'#172554',
        points: [
          'Palkkio maksetaan tilillesi',
          'Maksuliikenne tapahtuu kevytyrittäjäpalvelun kautta',
          'Viikoittainen tilityssykli',
        ],
      },
      {
        icon: Building2,
        label: 'Yritys',
        color: '#16a34a',
        bg:    '#f0fdf4',
        darkBg:'#052e16',
        points: [
          'Palkkio maksetaan yrityksesi tilille',
          'Lisää Y-tunnus ja IBAN profiiliisi',
          'Lasku tai suoramaksu sopimuksen mukaan',
        ],
      },
    ],
  },
  {
    icon: GitBranch,
    color: '#0891b2',
    bg:    '#ecfeff',
    darkBg:'#083344',
    title: 'Haluatko lisää kuljetettavaa?',
    intro: 'Pakettihuolto on vasta aloittanut, ja suurin osa kuljetuksista keskittyy pk-seudulle. Kuljetusten määrä ja hajonta kasvaa vähitellen — ja voit itsekin auttaa tässä.',
    steps: [
      {
        icon: null,
        label: 'Löydä asiakas',
        desc: 'Etsi somesta tai Googlen kautta henkilöitä, jotka tarvitsevat kuljetuksen. Hyviä paikkoja ovat esimerkiksi Facebookin rahtipörssit - näissä on päivittäin sopivia kuljetuspyyntöjä.',
      },
      {
        icon: null,
        label: 'Lähetä oma linkki',
        desc: 'Kurkkaa ensin Pakettihuollosta, paljonko kyseinen kuljetus maksaisi. Lähetä sitten henkilölle viesti, jossa mukana lähetyslinkkisi tai tee lähetys kokonaan hänen puolestaan.',
      },
      {
        icon: null,
        label: 'Kerää palkkiot',
        desc: 'Kun lähetys on suoritettu, saat vähintään 10 % hinnasta asiakashankintapalkkiona. Jos itse hoidat kuljetuksen, saat lisäksi suorituspalkkion. Jos jaat kuljetuksen toiselle kuriiirille, saat sekä asiakashankinta- että referointipalkkion.',
      },
    ],
    note: 'Jokainen uusi asiakas kasvattaa koko verkoston arvoa — ja tienaat siitä joka kerta.',
    noteColor: '#0891b2',
  },
  {
    icon: CheckCircle2,
    color: '#16a34a',
    bg:    '#f0fdf4',
    darkBg:'#052e16',
    title: 'Kaikki selvää — tervetuloa!',
    intro: 'Olet valmis aloittamaan. Täydennä profiilisi tiedot ja odota ensimmäistä toimeksiantoa alueeltasi.',
    checklist: [
      'Kuljetusalue asetettu profiiliin',
      'Kuljetusvälineet valittu',
      'IBAN-tilinumero lisätty',
    ],
  },
];

// ─── StepCard ─────────────────────────────────────────────────────────────────

const StepCard = ({ step }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const Icon   = step.icon;
  const bg     = isDark ? step.darkBg : step.bg;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

      {/* Icon + title */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{
          width: 48, height: 48, borderRadius: '14px', flexShrink: 0,
          bgcolor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: '1px solid', borderColor: alpha(step.color, 0.2),
        }}>
          <Icon size={24} color={step.color} strokeWidth={1.75} />
        </Box>
        <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.25 }}>
          {step.title}
        </Typography>
      </Box>

      {/* Intro paragraph */}
      {step.intro && (
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
          {step.intro}
        </Typography>
      )}

      {/* Term → definition blocks */}
      {step.sections?.map((s) => (
        <Box key={s.term} sx={{
          pl: 1.5,
          borderLeft: '3px solid',
          borderColor: alpha(step.color, 0.4),
        }}>
          <Typography variant="body2" fontWeight={700} gutterBottom>
            {s.term}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
            {s.body}
          </Typography>
        </Box>
      ))}

      {/* Numbered process steps */}
      {step.steps?.map((s, i) => (
        <Box key={s.label} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
          <Box sx={{
            mt: 0.25, width: 28, height: 28, borderRadius: '8px', flexShrink: 0,
            bgcolor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '1px solid', borderColor: alpha(step.color, 0.2),
          }}>
            <Typography variant="caption" fontWeight={800} sx={{ color: step.color }}>
              {i + 1}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" fontWeight={700}>{s.label}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
              {s.desc}
            </Typography>
          </Box>
        </Box>
      ))}

      {/* Callout note */}
      {step.note && (
        <Box sx={{
          p: 1.5, borderRadius: '10px',
          bgcolor: alpha(step.noteColor || step.color, 0.07),
          border: '1px solid', borderColor: alpha(step.noteColor || step.color, 0.18),
        }}>
          <Typography variant="body2" sx={{ lineHeight: 1.65, color: 'text.secondary' }}>
            {step.note}
          </Typography>
        </Box>
      )}

      {/* Payment type cards */}
      {step.paymentRows?.map((row) => {
        const RIcon = row.icon;
        const rowBg = isDark ? row.darkBg : row.bg;
        return (
          <Box key={row.label} sx={{
            p: 1.75, borderRadius: '12px',
            border: '1px solid', borderColor: alpha(row.color, 0.2),
            bgcolor: alpha(row.color, 0.04),
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Box sx={{
                width: 24, height: 24, borderRadius: '6px', flexShrink: 0,
                bgcolor: rowBg, display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <RIcon size={14} color={row.color} />
              </Box>
              <Typography variant="body2" fontWeight={700} color={row.color}>
                {row.label}
              </Typography>
            </Box>
            <Stack spacing={0.5}>
              {row.points.map((p) => (
                <Box key={p} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <Typography variant="body2" color={row.color} sx={{ mt: '1px', flexShrink: 0 }}>·</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>{p}</Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        );
      })}

      {/* Completion checklist */}
      {step.checklist?.map((item) => (
        <Box key={item} sx={{ display: 'flex', gap: 1.25, alignItems: 'center' }}>
          <CheckCircle2 size={16} color={step.color} style={{ flexShrink: 0 }} />
          <Typography variant="body2" color="text.secondary">{item}</Typography>
        </Box>
      ))}
    </Box>
  );
};

// ─── CourierBriefing ──────────────────────────────────────────────────────────

export const CourierBriefing = ({ user, userData, onProfileNavigate }) => {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDark   = theme.palette.mode === 'dark';

  const [open,       setOpen]       = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [marking,    setMarking]    = useState(false);
  const [dismissed,  setDismissed]  = useState(false);

  const isCourier = userData?.mode === 'courier' ||
                    userData?.roles?.includes('courier') ||
                    userData?.roles?.includes('specialCourier');
  const hasRead   = !!userData?.briefingRead;

  if (!isCourier || hasRead || dismissed) return null;

  const handleOpen  = () => { setActiveStep(0); setOpen(true); };
  const handleClose = () => setOpen(false);
  const handleNext  = () => setActiveStep((s) => Math.min(s + 1, STEPS.length - 1));
  const handleBack  = () => setActiveStep((s) => Math.max(s - 1, 0));

  const handleFinish = async () => {
    setMarking(true);
    try {
      if (user?.uid) await updateUserProfile(user.uid, { briefingRead: true });
      setOpen(false);
      setDismissed(true);
    } catch (err) {
      console.error('Failed to mark briefing as read:', err);
      setOpen(false);
      setDismissed(true);
    } finally {
      setMarking(false);
    }
  };

  const handleLastCta = () => {
    handleFinish();
    onProfileNavigate?.('/profile');
  };

  const isFirst = activeStep === 0;
  const isLast  = activeStep === STEPS.length - 1;
  const step    = STEPS[activeStep];

  // Amber/warning tones for the courier pill
  const pillBg    = isDark ? '#451a03' : '#78350f';
  const pillLabel = isDark ? '#fde68a' : '#fef3c7';
  const pillIcon  = isDark ? '#fcd34d' : '#fde68a';
  const ctaBg     = '#d97706';

  return (
    <>
      {/* ── Floating pill banner ─────────────────────────────────────────── */}
      <Box
        onClick={handleOpen}
        role="button"
        aria-label="Avaa kuriiriperehdytys"
        sx={{
          position: 'fixed',
          bottom: 24,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: (t) => t.zIndex.appBar - 1,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1.25,
          bgcolor: pillBg,
          border: '1px solid rgba(255,255,255,0.10)',
          borderRadius: '999px',
          px: 1.25,
          py: 1,
          cursor: 'pointer',
          userSelect: 'none',
          whiteSpace: 'nowrap',
          boxShadow: '0 4px 20px rgba(0,0,0,0.18)',
          transition: 'opacity 0.2s',
          '&:hover': { opacity: 0.93 },
        }}
      >
        {/* Icon circle */}
        <Box sx={{
          width: 28, height: 28, borderRadius: '50%',
          bgcolor: 'rgba(252,211,77,0.15)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <BookOpen size={13} color={pillIcon} />
        </Box>

        {/* Label */}
        <Typography
          variant="caption"
          fontWeight={700}
          sx={{ color: pillLabel, letterSpacing: 0.2, fontSize: '0.75rem' }}
        >
          Uusi kuriiri? Lue tämä ensin
        </Typography>

        {/* Inner CTA pill */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 0.5,
          bgcolor: ctaBg, borderRadius: '999px',
          px: 1.25, py: 0.4, flexShrink: 0,
        }}>
          <Typography variant="caption" fontWeight={700} sx={{ color: '#fff', fontSize: '0.7rem' }}>
            Avaa
          </Typography>
          <ArrowRight size={11} color="#fff" />
        </Box>
      </Box>

      {/* ── Modal — centered ─────────────────────────────────────────────── */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: isMobile ? 0 : '20px',
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
          },
        }}
      >
        {/* ── Sticky header ── */}
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 2.5, pt: 2, pb: 1.5,
          borderBottom: '1px solid', borderColor: 'divider',
          bgcolor: 'background.paper',
          position: 'sticky', top: 0, zIndex: 1,
        }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <BookOpen size={16} />
            <Typography variant="subtitle2" fontWeight={700}>Kuriiriperehdytys</Typography>
            <Typography variant="caption" color="text.disabled">
              {activeStep + 1} / {STEPS.length}
            </Typography>
          </Stack>
          <IconButton size="small" onClick={handleClose} sx={{ borderRadius: '8px' }}>
            <X size={16} />
          </IconButton>
        </Box>

        {/* ── Scrollable body ── */}
        <DialogContent sx={{ px: { xs: 2.5, sm: 3.5 }, py: 3 }}>
          <StepCard step={step} />
        </DialogContent>

        {/* ── Dot stepper ── */}
        <MobileStepper
          variant="dots"
          steps={STEPS.length}
          position="static"
          activeStep={activeStep}
          sx={{
            justifyContent: 'center',
            bgcolor: 'transparent',
            pb: 0, pt: 0.5,
            '& .MuiMobileStepper-dot': {
              width: 6, height: 6, mx: 0.4,
              bgcolor: 'action.disabled',
            },
            '& .MuiMobileStepper-dotActive': {
              bgcolor: step.color,
              width: 18, borderRadius: '3px',
              transition: 'width 0.25s ease, background-color 0.25s ease',
            },
          }}
          nextButton={null}
          backButton={null}
        />

        {/* ── Actions ── */}
        <DialogActions sx={{ px: { xs: 2.5, sm: 3.5 }, py: 2, gap: 1 }}>
          <Button
            size="small"
            onClick={handleBack}
            disabled={isFirst}
            startIcon={<ChevronLeft size={16} />}
            sx={{ borderRadius: '10px', textTransform: 'none', minWidth: 90 }}
          >
            Takaisin
          </Button>

          <Box sx={{ flex: 1 }} />

          {isLast ? (
            <Stack direction="row" spacing={1}>
              {step.ctaPath && (
                <Button
                  variant="outlined"
                  size="small"
                  endIcon={<ArrowRight size={15} />}
                  onClick={handleLastCta}
                  disabled={marking}
                  sx={{ borderRadius: '10px', textTransform: 'none' }}
                >
                  {step.cta}
                </Button>
              )}
              <Button
                variant="contained"
                size="small"
                endIcon={<CheckCircle2 size={15} />}
                onClick={handleFinish}
                disabled={marking}
                sx={{
                  borderRadius: '10px', textTransform: 'none',
                  fontWeight: 700, boxShadow: 'none',
                  '&:hover': { boxShadow: 'none' },
                }}
              >
                Selvä!
              </Button>
            </Stack>
          ) : (
            <Stack direction="row" spacing={1} alignItems="center">
              {step.ctaPath && (
                <Button
                  size="small"
                  variant="outlined"
                  endIcon={<ArrowRight size={14} />}
                  onClick={() => onProfileNavigate?.(step.ctaPath)}
                  sx={{ borderRadius: '10px', textTransform: 'none', fontSize: '0.8rem' }}
                >
                  {step.cta}
                </Button>
              )}
              <Button
                variant="contained"
                size="small"
                endIcon={<ChevronRight size={16} />}
                onClick={handleNext}
                sx={{
                  borderRadius: '10px', textTransform: 'none',
                  fontWeight: 700, boxShadow: 'none',
                  bgcolor: step.color,
                  '&:hover': { boxShadow: 'none', filter: 'brightness(0.9)', bgcolor: step.color },
                }}
              >
                Seuraava
              </Button>
            </Stack>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CourierBriefing;